import { Link } from "react-router-dom";
import { Phone, ArrowRight, ShieldCheck, HeartHandshake, Ruler, Mountain } from "lucide-react";
import { Reveal, MaskedLines, Marquee } from "@/components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL } from "@/data/site";

const STORY = [
  {
    title: "Raised In The Valley",
    copy: "Big Bear Heating & Air wasn't built in a boardroom. It was built in Manteca attics in July and Tracy crawlspaces in January. We started this company because Central Valley families and business owners deserved an HVAC contractor that treats a 60-mile radius like a neighborhood — because to us, it is one.",
  },
  {
    title: "The Technician Standard",
    copy: "Every Bear tech is factory-trained, certified, background-checked, and held to a simple rule: leave every system safer, cleaner, and better documented than we found it. Boot covers on. Drop cloths down. Photos of the work before we leave. No exceptions.",
  },
  {
    title: "Honesty, In Writing",
    copy: "You'll never get a vague estimate or a surprise line item from us. We diagnose, we show you what we found, and we quote a flat, upfront price before any work begins. If a repair will outlive its value, we'll tell you — even when a replacement costs us the easy sale.",
  },
  {
    title: "Specialists Where It Counts",
    copy: "Mobile homes run on different equipment, different ductwork, and tighter clearances — and most shops quietly hope you won't call. We went the other way and made it a pillar of the business. Mobile home AC and heating repair, full replacements, crossover ductwork, specialized maintenance: this is home turf.",
  },
];

const VALUES = [
  [ShieldCheck, "Licensed & Insured", "Fully credentialed for residential, mobile home & light commercial work."],
  [HeartHandshake, "Neighbor First", "We live here. Your word-of-mouth is worth more than any ad."],
  [Ruler, "Measured, Not Guessed", "Load calculations and real diagnostics — never rule-of-thumb pricing."],
];

const About = () => (
  <>
    <section data-testid="about-hero" className="relative overflow-hidden bg-bear-ink py-24 text-white lg:py-32">
      <div className="pointer-events-none absolute -right-8 top-10 select-none font-display text-[24vw] leading-none text-stroke-ghost lg:text-[17rem]">
        DEN
      </div>
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <Mountain className="h-4 w-4 text-bear-gold" />
          <span className="font-head text-xs font-bold uppercase tracking-[0.32em] text-bear-gold">
            About Big Bear
          </span>
        </div>
        <h1 className="mt-6 font-display uppercase leading-[0.92] tracking-wide">
          <MaskedLines
            delay={0.15}
            lineClassName="text-[clamp(2.8rem,8.5vw,7.5rem)]"
            lines={[
              <span key="a">Built On</span>,
              <span key="b" className="text-bear-cyan">Manteca</span>,
              <span key="c">Roots<span className="text-bear-gold">.</span></span>,
            ]}
          />
        </h1>
        <Reveal delay={0.6}>
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-white/65 sm:text-lg">
            A local crew with a craftsman's chip on its shoulder — serving
            homeowners, mobile home communities, and small businesses across
            Central California's 60-mile comfort zone.
          </p>
        </Reveal>
      </div>
    </section>

    <Marquee
      className="border-y border-white/10 bg-bear-ink py-5"
      itemClassName="font-head text-sm font-semibold uppercase tracking-[0.24em] text-white/50"
      items={["Rugged Reliability", "Upfront Pricing", "Certified Techs", "Mobile Home Authority", "Fast Dispatch", "Weekend Coverage"]}
    />

    <section className="bg-bear-paper py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-start gap-16 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr]">
        <Reveal>
          <div className="spotlight-photo lg:sticky lg:top-32">
            <img
              src="/images/tech-gauges.jpg"
              alt="Big Bear HVAC technician checking refrigerant gauges on an outdoor AC condenser"
              loading="lazy"
              className="aspect-[4/5] w-full rounded-md bg-bear-ink2 object-cover shadow-[0_40px_80px_rgba(0,0,0,0.35)]"
              style={{ clipPath: "polygon(0 6%, 100% 0, 100% 94%, 0 100%)" }}
            />
            <div className="absolute bottom-6 right-6 rounded-md bg-bear-ink/90 px-5 py-4 backdrop-blur">
              <p className="font-display text-3xl tracking-wide text-bear-cyan">EST. LOCAL</p>
              <p className="font-head text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
                1351 Shaefer St, Manteca
              </p>
            </div>
          </div>
        </Reveal>
        <div>
          {STORY.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <div data-testid={`about-chapter-${i + 1}`} className="group border-t border-black/10 py-10 first:border-t-0 first:pt-0">
                <span className="font-display text-5xl tracking-wide text-black/10 transition-colors duration-500 group-hover:text-bear-gold">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h2 className="mt-4 font-head text-2xl font-bold text-bear-ink">{s.title}</h2>
                <p className="mt-4 text-base leading-relaxed text-bear-slate">{s.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-bear-ink py-24 text-white lg:py-28">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <Reveal>
          <h2 className="font-display text-4xl uppercase tracking-wide sm:text-5xl">
            What We Refuse <span className="text-bear-gold">To Compromise.</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-5 md:grid-cols-3">
          {VALUES.map(([Icon, t, c], i) => (
            <Reveal key={t} delay={i * 0.1}>
              <div data-testid={`about-value-${i + 1}`} className="h-full rounded-md border border-white/10 bg-white/5 p-8 backdrop-blur transition-colors duration-500 hover:border-bear-cyan/50">
                <Icon className="h-8 w-8 text-bear-cyan" />
                <h3 className="mt-5 font-head text-lg font-bold">{t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{c}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-16 flex flex-wrap items-center gap-4">
            <a
              href={PHONE_TEL}
              data-testid="about-call-cta"
              className="flex items-center gap-3 rounded-full bg-bear-red px-8 py-4 font-head text-base font-bold text-white transition-transform hover:scale-105"
            >
              <Phone className="h-5 w-5" /> Call {PHONE_DISPLAY}
            </a>
            <Link
              to="/services"
              data-testid="about-services-cta"
              className="group flex items-center gap-2 rounded-full border border-white/20 px-8 py-4 font-head text-base font-bold text-white transition-colors hover:border-bear-cyan hover:text-bear-cyan"
            >
              See Our Services
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </Reveal>
      </div>
    </section>
  </>
);

export default About;
